import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import PersonalDetails from "../employee/forms/PersonalDetails";
import KycDetails from "../employee/forms/KycDetails";
import QualificationDetails from "../employee/forms/QualificationDetails";
import WorkExperience from "../employee/forms/WorkExperience";
import OtherDetails from "../employee/forms/OtherDetails";
import {
  FinalizeOnboardingService,
  SyncProfileService,
  UploadDocumentsService,
} from "../services/ApiService";

export default function EmployeeOnboarding() {
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ---------------- CENTRALIZED STATE ---------------- */
  const [personalDetails, setPersonalDetails] = useState({});
  const [kycDetails, setKycDetails] = useState({});
  const [qualifications, setQualifications] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);
  const [otherDetails, setOtherDetails] = useState({});

  /* ---------------- NAVIGATION ---------------- */
  const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, 5));
  const handlePrev = () => setActiveStep((prev) => Math.max(prev - 1, 1));

  useEffect(() => {
    document.title = "Employee Panel | Code 51";
  }, []);

  /* ---------------- FINAL SUBMIT ---------------- */
  const handleFinalSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      console.log("🚀 FINAL SUBMIT STARTED");

      // 1. MAP JSON DATA
      const profilePayload = {
        personal_details: {
          mobile_number: personalDetails.phone || "",
          alternative_no: personalDetails.alternatePhone || null,
          email: personalDetails.email || "",
          date_of_birth: personalDetails.dob || null,
          father_name: personalDetails.fatherName || "",
          mother_name: personalDetails.motherName || "",
          gender: (personalDetails.gender || "OTHER").toUpperCase(),
          blood_group: personalDetails.bloodGroup || "",
          permanent_address: personalDetails.permanentAddress || "",
          current_address: personalDetails.residentialAddress || "",
        },
        kyc_details: {
          aadhaar_number: kycDetails.aadhaarNumber || "",
          pan_number: (kycDetails.panNumber || "").toUpperCase(),
          bank_account_no: kycDetails.bankAccount || "",
          ifsc_code: kycDetails.ifscCode || "",
          passport_number: kycDetails.passportNumber || null,
        },
        // Update the education mapping in handleFinalSubmit
        education: qualifications.map((q) => ({
          level: q.degree, // ✅ Send the exact enum value (10th, 12th, Bachelor's, etc.)
          other_level_text: q.degree === "Other" ? q.customDegree : null, // ✅ Changed field name
          institution_name: q.schoolName,
          specialization: q.specialization || null,
          year_of_passing: parseInt(q.year, 10),
          percentage_marks: parseFloat(q.percentage),
        })),

        work_experience: workExperiences.map((w) => ({
          company_name: w.company,
          role: w.designation,
          start_date: w.fromDate,
          end_date: w.toDate || null,
          last_salary: parseFloat(w.lastSalary) || 0,
        })),
        languages: otherDetails.languagesAndInfo || "",
        declaration_accepted: otherDetails.confirmation || false,
        declaration_place: otherDetails.place || "",
        declaration_name: otherDetails.signatureName || "",
        declaration_date: otherDetails.date || "",
      };

      console.log("📤 Syncing profile data...");
      await SyncProfileService(profilePayload);
      console.log("✅ Profile synced successfully");

      // 2. MAP FILES
      const formData = new FormData();
      let hasFiles = false;

      // --- KYC Files ---
      if (kycDetails.aadhaarFile) {
        console.log("📎 Adding Aadhaar file:", kycDetails.aadhaarFile.name);
        formData.append("aadhaar", kycDetails.aadhaarFile);
        hasFiles = true;
      }
      if (kycDetails.panFile) {
        console.log("📎 Adding PAN file:", kycDetails.panFile.name);
        formData.append("pan", kycDetails.panFile);
        hasFiles = true;
      }
      if (kycDetails.bankFile) {
        console.log("📎 Adding Passbook file:", kycDetails.bankFile.name);
        formData.append("passbook", kycDetails.bankFile);
        hasFiles = true;
      }

      // ✅ Updated: Qualification Files with corrected degree mapping
      const degreeMapping = {
        "10th": "tenth",
        "12th": "twelfth",
        "Bachelor's": "bachelor", // ✅ Changed
        Diploma: "diploma",
        "Master's": "masters", // ✅ Changed
        "Training/Internship": "training",
      };

      qualifications.forEach((edu) => {
        if (edu.file) {
          let paramName = degreeMapping[edu.degree];

          if (!paramName && edu.degree === "Other" && edu.customDegree) {
            paramName = edu.customDegree
              .toLowerCase()
              .replace(/[^a-z0-9]/g, "_");
          }

          if (paramName) {
            console.log(
              `📎 Adding qualification file (${paramName}):`,
              edu.file.name
            );
            formData.append(paramName, edu.file);
            hasFiles = true;
          }
        }
      });

      // --- Work Experience / Job Files ---
      const docTypeMapping = {
        "Joining Letter": "joining_letter",
        "Experience Letter": "experience_letter",
        "Salary Slip": "salary_slip",
        "Offer Letter": "offer_letter",
        "Relieving Letter": "relieving_letter",
        "Appointment Letter": "appointment_letter",
        "Resignation Letter": "resignation_letter",
      };

      workExperiences.forEach((work) => {
        work.documents?.forEach((doc) => {
          if (doc.file && docTypeMapping[doc.type]) {
            const paramName = docTypeMapping[doc.type];
            console.log(`📎 Adding work doc (${paramName}):`, doc.file.name);
            formData.append(paramName, doc.file);
            hasFiles = true;
          }
        });
      });

      // ✅ ONLY upload if there are files
      if (hasFiles) {
        console.log("➡️ Uploading documents...");

        // Debug: Log all FormData entries
        for (let [key, value] of formData.entries()) {
          console.log(`FormData: ${key} =`, value);
        }

        try {
          await UploadDocumentsService(formData);
          console.log("✅ Documents uploaded successfully");
        } catch (uploadError) {
          console.error("❌ Document upload failed:", uploadError);
          throw new Error(`Document upload failed: ${uploadError.message}`);
        }
      } else {
        console.log("ℹ️ No documents to upload, skipping...");
      }

      // 3. FINALIZE
      console.log("🏁 Finalizing onboarding...");
      await FinalizeOnboardingService();
      console.log("✅ Onboarding finalized");

      alert("🎉 Onboarding Completed Successfully!");
      window.location.href = "/employee/profile";
    } catch (err) {
      console.error("❌ SUBMIT FAILED:", err);

      // Better error message
      let errorMessage = "Submission failed. ";
      if (err.message.includes("Failed to fetch")) {
        errorMessage +=
          "Cannot connect to server. Please check if the backend is running.";
      } else {
        errorMessage += err.message;
      }

      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------------- RENDER ---------------- */
  const renderForm = () => {
    const props = {
      nextStep: handleNext,
      prevStep: handlePrev,
      isLastStep: false,
      isFirstStep: activeStep === 1,
    };

    switch (activeStep) {
      case 1:
        return (
          <PersonalDetails
            data={personalDetails}
            update={setPersonalDetails}
            {...props}
            isFirstStep={true}
          />
        );
      case 2:
        return (
          <KycDetails data={kycDetails} update={setKycDetails} {...props} />
        );
      case 3:
        return (
          <QualificationDetails
            data={qualifications}
            update={setQualifications}
            {...props}
          />
        );
      case 4:
        return (
          <WorkExperience
            data={workExperiences}
            update={setWorkExperiences}
            {...props}
          />
        );
      case 5:
        return (
          <OtherDetails
            data={otherDetails}
            update={setOtherDetails}
            prevStep={handlePrev}
            isLastStep={true}
            onFinalSubmit={handleFinalSubmit}
            isSubmitting={isSubmitting}
            personalDetails={personalDetails}
            kycDetails={kycDetails}
            qualifications={qualifications}
            workExperiences={workExperiences}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex justify-center items-center p-6">
      <div className="w-full max-w-6xl h-[90vh] bg-white rounded-[40px] shadow-xl flex overflow-hidden">
        <Sidebar activeStep={activeStep} setActiveStep={setActiveStep} />

        <div className="flex-1 flex flex-col h-full">
          <div className="px-10 pt-10 pb-4 bg-white border-b border-gray-100">
            <h1 className="text-2xl font-bold text-gray-800">
              Employee Registration
            </h1>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mt-1">
              Step {activeStep} of 5
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar px-10 py-8">
            {renderForm()}
          </div>
        </div>
      </div>
    </div>
  );
}
